import React from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { ArrowRight, Calendar, Users } from 'lucide-react';

interface RelatedJourney {
  id: number;
  title: Record<string, string>;
  subtitle: Record<string, string>;
  description: Record<string, string>;
  image: string;
  duration: string;
  groupSize: string;
  startingPrice: string;
  currency: string;
}

interface TravelGuideRelatedTourWidgetProps {
  relatedJourneys: RelatedJourney[];
  locale: string;
}

export default function TravelGuideRelatedTourWidget({
  relatedJourneys,
  locale,
}: TravelGuideRelatedTourWidgetProps) {
  // Only show the first related journey to keep it small
  if (!relatedJourneys || relatedJourneys.length === 0) {
    return null;
  }

  const journey = relatedJourneys[0];
  const journeyTitle = journey.title[locale] || journey.title.en || '';
  const journeySubtitle = journey.subtitle[locale] || journey.subtitle.en || '';
  const journeyDescription = journey.description[locale] || journey.description.en || '';

  return (
    <Link
      href={`/${locale}/signature-journeys/${journey.id}`}
      className="group block bg-gradient-to-br from-primary-50 to-primary-100/50 rounded-lg border border-primary-200/50 p-4 sm:p-5 hover:shadow-md transition-all duration-200 hover:border-primary-300"
    >
      <div className="flex flex-col sm:flex-row items-start gap-4 sm:gap-3">
        <div className="relative w-full sm:w-24 h-40 sm:h-24 md:w-28 md:h-28 flex-shrink-0 rounded-lg overflow-hidden">
          <Image
            src={journey.image}
            alt={journeyTitle}
            fill
            className="object-cover group-hover:scale-105 transition-transform duration-200"
            sizes="(max-width: 640px) 100vw, 112px"
          />
        </div>
        <div className="flex-1 min-w-0 w-full">
          <div className="flex items-start justify-between gap-2 mb-2">
            <h3 className="text-base sm:text-lg font-semibold text-primary-900 group-hover:text-primary-700 transition-colors line-clamp-2">
              {journeyTitle}
            </h3>
            <ArrowRight className="h-5 w-5 text-primary-600 flex-shrink-0 mt-0.5 group-hover:translate-x-0.5 transition-transform" />
          </div>
          {journeySubtitle && (
            <p className="text-sm text-gray-600 line-clamp-1 mb-2">
              {journeySubtitle}
            </p>
          )}
          {journeyDescription && (
            <p className="text-sm text-gray-600 line-clamp-2 mb-3">
              {journeyDescription}
            </p>
          )}
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-gray-500">
            <span className="flex items-center gap-1.5">
              <Calendar className="h-4 w-4" />
              <span>{journey.duration}</span>
            </span>
            <span className="flex items-center gap-1.5">
              <Users className="h-4 w-4" />
              <span>{journey.groupSize}</span>
            </span>
            <span className="text-primary-600 font-semibold sm:ml-auto">
              From {journey.currency} {journey.startingPrice}
            </span>
          </div>
        </div>
      </div>
    </Link>
  );
}

